/**
 * END EVENT readiness: can every bout on the card be recorded?
 *
 * END EVENT (manage-event.jsx → the updateRecords callable) writes one
 * eventResults entry per bout. A bout is ready when it is a DRAW, or has a WIN
 * pick and a method (TKO / KO / SUBMISSION / DECISION). Anything else makes
 * updateRecords write `winBy: undefined` (or `winnerId: undefined`): Firestore
 * rejects the whole batch, the event is not ended and the admin only sees
 * "Something went wrong." So the button stays disabled until every bout is
 * ready, and says which ones are not.
 *
 * Pure, cheap: safe to call on every render.
 */

/**
 * A pair with a pulled-out fighter is a cancelled match: the judges don't see
 * it, END EVENT records nothing for it and the Partner API calls it
 * cancelled. matchWith is kept, so restoring the fighter brings it back.
 */
export const isPulledOutPair = (pair) =>
    !!(pair?.fighterA?.isPulledOut || pair?.fighterB?.isPulledOut);

/**
 * The bouts END EVENT sends to updateRecords, as [pairKey, pair] entries:
 * every matched pair except the cancelled (pulled-out) ones.
 */
export const endEventPairEntries = (matchedFighters) =>
    Object.entries(matchedFighters || {}).filter(
        ([, pair]) => !isPulledOutPair(pair)
    );

/**
 * What one bout still needs before END EVENT can record it:
 * null when ready, "result" when neither WIN nor DRAW is picked (a method on
 * its own doesn't count), "method" when a winner has no method. A draw needs
 * no method.
 */
export const boutResultGap = (pair) => {
    if (pair?.isDraw) return null;
    if (!pair?.winId) return "result";
    if (!pair?.winBy) return "method";
    return null;
};

/**
 * @param {object} matchedFighters  Manage Event's picks, keyed by pairKey
 * @param {string[]} [orderedPairKeys]  every bout in running order (pulled-out
 *   ones included), so `n` is the number the card shows
 * @returns {{ ready: boolean, total: number, left: number,
 *   missing: Array<{ pairKey: string, n: number|null, needs: "result"|"method" }> }}
 */
export const endEventReadiness = (matchedFighters, orderedPairKeys = []) => {
    const numberOf = new Map(
        (orderedPairKeys || []).map((pairKey, index) => [pairKey, index + 1])
    );
    const entries = endEventPairEntries(matchedFighters);
    const missing = entries
        .map(([pairKey, pair]) => ({
            pairKey,
            n: numberOf.get(pairKey) ?? null,
            needs: boutResultGap(pair),
        }))
        .filter((bout) => bout.needs)
        .sort((a, b) => (a.n ?? Infinity) - (b.n ?? Infinity));
    return {
        ready: missing.length === 0,
        total: entries.length,
        left: missing.length,
        missing,
    };
};

const MAX_LISTED = 8;

/**
 * The words under a blocked END EVENT: a one-line `hint` for the page and a
 * longer `title` (tooltip) that says what each bout lacks. Both are "" when
 * the card is ready.
 */
export const endEventBlockedText = (readiness) => {
    if (!readiness || readiness.ready) return { hint: "", title: "" };
    const numbered = readiness.missing.filter((bout) => bout.n != null);
    const listed = numbered.slice(0, MAX_LISTED).map((bout) => bout.n);
    const more = numbered.length - listed.length;
    const which = listed.length
        ? ` (${listed.length === 1 && more === 0 ? "match" : "matches"} ${listed.join(", ")}${more > 0 ? ` +${more} more` : ""})`
        : "";
    const hint = `Pick a result and method for every match · ${readiness.left} left${which}`;
    const perBout = numbered
        .map(
            (bout) =>
                `Match ${bout.n}: ${bout.needs === "method" ? "winner picked, no method" : "no result picked"}`
        )
        .join(" · ");
    const title = `END EVENT unlocks when every match has WIN plus TKO / KO / SUBMISSION / DECISION, or DRAW.${perBout ? ` ${perBout}.` : ""}`;
    return { hint, title };
};
